import { combineReducers } from 'redux';
import { difference } from 'lodash';

/**
 * Base callback to be used by reducerById
 * @param {*} newState The state to be parsed.
 * @param {*} action The action containing the response to check the state for.
 * @param {*} checkDelete To be called after each item is placed into the state.
 * If check is truthy then the object is removed from the state.
 */
export const baseCallback = (newState, action, checkDelete) => {
  Object.keys(action.response).forEach(k => {
    if (newState[k]) {
      newState[k] = {
        ...newState[k],
        ...action.response[k]
      };
      if (checkDelete(newState[k])) {
        delete newState[k];
      }
    } else {
      newState[k] = action.response[k];
    }
  });
  return newState;
};

/**
 * Generic reducer used to store mapped objects. Ex: Map of job id to job.
 * @param {string} successAction - The success action name
 * @param {string} deleteAction - The delete action name
 * @param {(o) => bool} checkDelete - to be called after each item is placed
 * into the state, if check is truthy then the object is removed from the state
 *  @param {function} checkState - to be called to check if the response is
 * already in state
 */
export const reducerById =
  (successAction, deleteAction, checkState = baseCallback) =>
    (state = {}, action) => {
      let newState;
      switch (action.type) {
        case successAction:
          newState = { ...state };
          return checkState(newState, action, () => false);
        case deleteAction:
          newState = { ...state };
          return checkState(newState, action, () => true);
        default:
          return state;
      }
    };

/**
 * Generic reducer used to store some caching information about requests. It stores if a
 * typed request is in progress and all arguments that have been requested since the
 * beginning of time in order to avoid requesting them again.
 * @param {string} requestName - The request name. Used to identify request types if there
 *   are multiple ways of requesting the same data. Ex: Requesting jobs by build ids could
 *   have a request name of byBuildId. NOTE: This must match the string provided to the
 *   apiActionDispatcher.
 * @param {{ request: string, success: string, failure: string }} actions - The various
 *   action names for different request events.
 */
export const cachedRequestReducer = (requestName, actions) => {
  const initialState = {
    inProgress: false,
    args: undefined,
    isDone: undefined
  };
  return (state = initialState, action) => {
    if (!action.requestName || action.requestName !== requestName) {
      return state;
    }

    switch (action.type) {
      case actions.request:
        return {
          inProgress: true,
          args: action.args,
          isDone: state.isDone
        };
      case actions.success:
        return {
          ...state,
          inProgress: false,
          isDone: action.isDone
        };
      case actions.failure:
        return {
          inProgress: false,
          isDone: action.isDone,
          args: state.args && action.args ?
            difference(state.args, action.args) :
            action.args
        };
      case actions.delete:
        return {
          ...state,
          inProgress: false,
          isDone: action.isDone
        };
      default:
        return state;
    }
  };
};

/**
 * Combines the byId reducer and the cached request reducer
 * @param {{ request: string, success: string, failure: string }} actions - The various
 *   action names for different request events.
 * @param {string} groupingName - This is simply used to name the reducer. So it will be
 *   the name of the property where your actual data is stored within state.
 * @param {string[]} requestNames - This will be used to create a cachedRequestReducer for
 *   each and is how a certain type of request will be mapped to its cached information.
 */
export const cachedRequestByIdReducer = (
  actions,
  groupingName = 'byId',
  requestNames = ['request'],
  reducer = () => reducerById(actions.success, actions.delete)
) => {
  const reducers = requestNames.reduce((result, rn) => {
    result[rn] = cachedRequestReducer(rn, actions);
    return result;
  }, { [groupingName]: reducer() });
  return combineReducers(reducers);
};


// WEBPACK FOOTER //
// src/reducers/helpers.js